import { initializeQuickBuyCards, disposeQuickBuyCards, closeQuickBuyDropdowns } from './quick-buy-card.js';
import { createCheckboxIndicator } from './checkbox-indicator.js';
const { QUESTIONS, DETAILS, STORE_KEY, validAnswers, stepsFor, questionFor, budgetInPence, suitableVariants, selectedProducts, allSuitableProducts, answersForSelection, reasonFor, resultContext } = await import(
  document.querySelector('[data-gift-finder]')?.dataset.giftMatchingUrl || './gift-finder-matching.js'
);

const currency = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 });
const ANSWER_NAMES = { budget: 'Budget', direction: 'Gift', grinder: 'Grinder', brew: 'Setup', caffeine: 'Caffeine', kind: 'Equipment', drive: 'Style', taste: 'Flavour', browseKind: 'Browse', espressoGrinder: 'Espresso grinder' };

// Custom pixels receive only fixed answer keys and catalogue IDs. Shopify's
// privacy API controls whether analytics events may be emitted at all.
function track(event, payload = {}) {
  try {
    if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() !== true) return;
    const analytics = window.Shopify?.analytics;
    if (typeof analytics?.publish !== 'function') return;
    Promise.resolve(analytics.publish(`sigma_gift_finder:${event}`, payload)).catch(() => {});
  } catch { /* Analytics is optional and never blocks the finder. */ }
}

function make(tag, className, content) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (content) node.textContent = content;
  return node;
}

function action(label, callback, primary = false) {
  const button = make('button', `button button--compact${primary ? ' button--primary' : ''} coffee-finder__button`, label);
  button.type = 'button';
  button.addEventListener('click', callback);
  return button;
}

function fitMobileResultRail(grid) {
  const compact = matchMedia('(max-width: 719px)');
  const cards = [...grid.children];
  const space = grid.parentElement;
  let frame = 0;
  const update = () => {
    frame = 0;
    if (!compact.matches) {
      space.style.removeProperty('block-size');
      return;
    }
    const style = getComputedStyle(grid);
    const height = Math.max(0, ...cards.map(card => card.getBoundingClientRect().height));
    const size = height + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    if (Math.abs(parseFloat(space.style.blockSize) - size) > 0.5 || !space.style.blockSize)
      space.style.blockSize = `${size}px`;
    if (grid.scrollTop) grid.scrollTop = 0;
  };
  const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
  const observer = new ResizeObserver(schedule);
  cards.forEach(card => observer.observe(card));
  compact.addEventListener('change', schedule);
  update();
  return () => {
    cancelAnimationFrame(frame);
    observer.disconnect();
    compact.removeEventListener('change', schedule);
  };
}

function initializeGiftAmbience(root, reducedMotion) {
  const layer = root.querySelector('[data-gift-ambient]');
  if (!layer) return;
  // A CDN placeholder has valid dimensions too, so check its URL as well as
  // loading failure. Never display a broken-image tile in the decorative field.
  layer.querySelectorAll('img').forEach(image => {
    const discard = () => image.closest('[data-ambient-item]')?.remove();
    if (image.currentSrc.includes('/no-image-') || image.src.includes('/no-image-') || (image.complete && !image.naturalWidth)) discard();
    else image.addEventListener('error', discard, { once: true });
  });
  let visible = false;
  const update = () => {
    const intro = root.dataset.finderState === 'intro';
    root.dataset.ambientRunning = String(intro && visible && !document.hidden && !reducedMotion.matches);
  };
  new IntersectionObserver(entries => { visible = entries[0].isIntersecting; update(); }).observe(root);
  new MutationObserver(update).observe(root, { attributes: true, attributeFilter: ['data-finder-state'] });
  document.addEventListener('visibilitychange', update);
  reducedMotion.addEventListener('change', update);
  update();
}

function init(root) {
  if (root.dataset.giftInitialized === 'true') return;
  const view = root.querySelector('[data-gift-view]');
  const data = root.querySelector('[data-gift-finder-products]');
  const status = root.querySelector('[data-gift-status]');
  if (!view || !data) return;
  let products;
  try { products = JSON.parse(data.textContent); } catch { return; }
  if (!Array.isArray(products)) return;
  root.dataset.giftInitialized = 'true';
  const iconFor = (name) => {
    const icon = make('span', 'button__icon');
    icon.setAttribute('aria-hidden', 'true');
    const template = root.querySelector(`template[data-gift-ui-icon="${name}"]`);
    if (template) icon.append(template.content.cloneNode(true));
    return icon;
  };
  const storageKey = `${STORE_KEY}:${root.id}`;
  let answers = {};
  let variantChoices = {};
  try {
    const saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
    if (saved?.version === 2 && validAnswers(saved.answers)) {
      answers = saved.answers;
      if (saved.variantChoices && typeof saved.variantChoices === 'object' && !Array.isArray(saved.variantChoices)) {
        variantChoices = Object.fromEntries(Object.entries(saved.variantChoices).filter(([productId, variantId]) => /^\d+$/.test(productId) && typeof variantId === 'string' && /^\d+$/.test(variantId)));
      }
    }
  } catch { /* Optional session convenience. */ }
  const persist = () => {
    try { sessionStorage.setItem(storageKey, JSON.stringify({ version: 2, answers, variantChoices })); } catch { /* The finder also works without storage. */ }
  };
  const announce = (message) => { if (status) status.textContent = message; };
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  initializeGiftAmbience(root, reducedMotion);
  let animations = [];
  let cleanups = [];
  const stopAnimations = () => { animations.forEach(animation => animation.cancel()); animations = []; view.style.removeProperty('overflow'); };
  const errorAnimations = new Map();
  let errorSoundUntil = 0;
  const clearErrorFeedback = (target) => {
    if (!target) return;
    target.classList.remove('is-gift-error');
    errorAnimations.get(target)?.cancel();
    errorAnimations.delete(target);
  };
  const showErrorFeedback = (target) => {
    if (!target) return;
    target.classList.add('is-gift-error');
    errorAnimations.get(target)?.cancel();
    if (!reducedMotion.matches) {
      // Match the bundle add-on cap feedback without a forced layout read.
      const shake = target.animate([
        { transform: 'translateX(0)' }, { transform: 'translateX(-3px)' },
        { transform: 'translateX(2px)' }, { transform: 'translateX(-1px)' },
        { transform: 'translateX(0)' },
      ], { duration: 240, easing: 'ease-in-out' });
      errorAnimations.set(target, shake);
      shake.finished.catch(() => {}).then(() => { if (errorAnimations.get(target) === shake) errorAnimations.delete(target); });
    }
    const now = performance.now();
    if (now >= errorSoundUntil) {
      try { window.SigmaThemeAudio?.playError(); } catch { /* Optional feedback never blocks recovery. */ }
    }
    errorSoundUntil = now + 300;
  };
  reducedMotion.addEventListener('change', () => {
    if (!reducedMotion.matches) return;
    stopAnimations();
    errorAnimations.forEach(animation => animation.cancel()); errorAnimations.clear();
  });

  // Reserve the largest question at the current width. Results are never sized
  // to this reserve and remain ordinary document content.
  const measureQuestions = () => {
    if (root.dataset.finderState !== 'question' || window.matchMedia('(max-width: 639px)').matches) return;
    const width = view.clientWidth;
    if (measuredWidth === width && questionSizeReady) return;
    const measurement = make('div');
    Object.assign(measurement.style, { position: 'absolute', visibility: 'hidden', pointerEvents: 'none', inlineSize: `${width}px`, contain: 'layout style' });
    measurement.setAttribute('aria-hidden', 'true');
    measurement.inert = true;
    const probes = [];
    for (const key of Object.keys(QUESTIONS).filter(key => key !== 'taste')) {
      const probe = make('div', 'gift-finder__pane');
      probe.style.minBlockSize = '0px';
      probe.append(make('p', 'coffee-finder__helper gift-finder__progress', 'Question 5'), make('h2', 'coffee-finder__title', QUESTIONS[key].title));
      if (key === 'budget' || key === 'direction' || key === 'caffeine') probe.append(make('p', 'coffee-finder__helper', key === 'budget' ? 'For the gift itself. Delivery and required accessories are extra.' : key === 'direction' ? 'Not sure? An easy gift is a good place to start.' : 'You can still choose either preference. If no coffee fits, we will explain why and show other gifts within your budget.'));
      const fieldset = make('fieldset', 'coffee-finder__fieldset');
      const options = make('div', 'coffee-finder__options');
      for (const [value, label] of QUESTIONS[key].options) {
        const option = make('div', 'coffee-finder__option');
        const copy = make('span', 'coffee-finder__option-copy');
        copy.append(make('span', 'coffee-finder__option-title', label));
        if (DETAILS[key]?.[value]) copy.append(make('span', 'coffee-finder__option-detail', DETAILS[key][value]));
        option.append(copy); options.append(option);
      }
      fieldset.append(options);
      if (key === 'budget') {
        const custom = make('div', 'gift-finder__custom');
        custom.append(make('label', '', 'Your budget in pounds'), make('input'));
        fieldset.append(custom);
      }
      fieldset.append(make('p', 'coffee-finder__validation', 'Enter an amount greater than £0, with up to two decimal places.'));
      probe.append(fieldset);
      probes.push(probe);
    }
    measurement.append(...probes);
    root.append(measurement);
    // Build all probes first, then read their geometry in one layout pass.
    const largest = Math.max(...probes.map(probe => Math.ceil(probe.getBoundingClientRect().height)));
    measurement.remove();
    measuredWidth = width;
    questionSizeReady = true;
    root.style.setProperty('--gift-question-size', `${largest}px`);
  };
  let measuredWidth = 0;
  let questionSizeReady = false;
  const layoutObserver = new ResizeObserver(() => {
    if (root.dataset.finderState !== 'question' || view.clientWidth === measuredWidth) return;
    stopAnimations(); measureQuestions();
  });
  layoutObserver.observe(view);
  document.fonts?.ready.then(() => { questionSizeReady = false; measureQuestions(); });
  const revealInPage = (element) => {
    if (!element || element.closest(':popover-open')) return;
    const target = element.closest('.coffee-finder__option, .gift-finder__acknowledgement') || element;
    const bounds = target.getBoundingClientRect();
    const top = document.querySelector('[data-header]')?.getBoundingClientRect().bottom || 130;
    if (bounds.top < top + 16 || bounds.bottom > innerHeight - 16) target.scrollIntoView({ block: 'nearest', behavior: reducedMotion.matches ? 'instant' : 'smooth' });
  };
  const focusWithin = (element) => {
    element?.focus({ preventScroll: true });
    revealInPage(element);
  };
  window.addEventListener('scroll', () => closeQuickBuyDropdowns(view), { passive: true });
  const showView = (content, { focus = true } = {}) => {
    const previousHeight = view.getBoundingClientRect().height;
    const previousScroll = window.scrollY;
    stopAnimations();
    errorAnimations.forEach(animation => animation.cancel()); errorAnimations.clear();
    cleanups.forEach(cleanup => cleanup()); cleanups = [];
    disposeQuickBuyCards(view);
    view.replaceChildren(content);
    const pane = content.querySelector('.gift-finder__pane');
    const nextHeight = view.getBoundingClientRect().height;
    if (!reducedMotion.matches) {
      if (Math.abs(previousHeight - nextHeight) > 1 && !window.matchMedia('(max-width: 639px)').matches) {
        view.style.overflow = 'clip';
        const resize = view.animate([
          { height: `${previousHeight}px` }, { height: `${nextHeight}px` },
        ], { duration: 240, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
        animations.push(resize);
        resize.finished.catch(() => {}).then(() => { if (animations.includes(resize)) view.style.removeProperty('overflow'); });
      }
      animations.push(pane.animate([
        { opacity: 0, transform: 'translateY(4px)' }, { opacity: 1, transform: 'translateY(0)' },
      ], { duration: 200, easing: 'cubic-bezier(0.25, 1, 0.5, 1)', fill: 'both' }));
    }
    const heading = content.querySelector('h2');
    heading.id = `${root.id}-title`; heading.tabIndex = -1;
    if (focus) {
      heading.focus({ preventScroll: true });
      const bounds = heading.getBoundingClientRect();
      if (bounds.top < 130 || bounds.bottom > innerHeight) heading.scrollIntoView({ block: 'start', behavior: reducedMotion.matches ? 'instant' : 'smooth' });
    } else window.scrollTo({ top: previousScroll, behavior: 'instant' });
  };
  const paneFor = (label) => {
    const pane = make('div', 'gift-finder__pane');
    pane.setAttribute('role', 'region'); pane.setAttribute('aria-label', label);
    return pane;
  };
  const validPath = () => {
    for (const key of stepsFor(answers, products)) {
      if (!questionFor(key, answers, products).options.some(([value]) => value === answers[key])) delete answers[key];
    }
    const path = stepsFor(answers, products);
    for (const key of ['grinder', 'brew', 'caffeine', 'kind', 'drive']) {
      if (!path.includes(key)) delete answers[key];
    }
    if (answers.direction !== 'coffee') delete answers.taste;
    return path.find(key => !answers[key]);
  };

  function renderQuestion(requestedKey, editing = false) {
    let key = requestedKey;
    const steps = stepsFor(answers, products);
    if (!steps.includes(key)) key = steps.find(step => !answers[step]) || 'budget';
    const question = questionFor(key, answers, products);
    const position = steps.indexOf(key);
    root.dataset.finderState = 'question';
    measureQuestions();
    const form = make('form', 'gift-finder__stage');
    form.noValidate = true;
    const pane = paneFor('Gift finder question');
    const progress = make('p', 'coffee-finder__helper gift-finder__progress', `Question ${position + 1} · ${position < 2 ? 'Getting started' : 'Finding the right fit'}`);
    const heading = make('h2', 'coffee-finder__title', question.title);
    const fieldset = make('fieldset', 'coffee-finder__fieldset');
    fieldset.setAttribute('aria-labelledby', `${root.id}-title`);
    const error = make('p', 'coffee-finder__validation');
    error.id = `${root.id}-error`;
    error.setAttribute('role', 'alert');
    const options = make('div', 'coffee-finder__options');
    question.options.forEach(([value, label]) => {
      const option = make('label', 'coffee-finder__option');
      const input = make('input', 'sigma-choice-radio');
      input.type = 'radio';
      input.name = `gift-${key}`;
      input.value = value;
      input.checked = answers[key] === value;
      input.setAttribute('aria-describedby', error.id);
      const copy = make('span', 'coffee-finder__option-copy');
      copy.append(make('span', 'coffee-finder__option-title', label));
      const detail = question.optionDetails?.[value] || DETAILS[key]?.[value];
      if (detail) copy.append(make('span', 'coffee-finder__option-detail', detail));
      option.append(input, createCheckboxIndicator(), copy);
      options.append(option);
    });
    fieldset.append(options);
    let custom;
    if (key === 'budget') {
      const helper = make('p', 'coffee-finder__helper', 'For the gift itself. Delivery and required accessories are extra.');
      pane.append(progress, heading, helper);
      custom = make('div', 'gift-finder__custom');
      const label = make('label', '', 'Your budget in pounds');
      const input = make('input');
      input.type = 'text';
      input.inputMode = 'decimal';
      input.enterKeyHint = 'next';
      input.autocomplete = 'off';
      input.placeholder = 'For example, 75';
      input.value = answers.customBudget || '';
      input.id = `${root.id}-budget`;
      input.setAttribute('aria-describedby', error.id);
      label.htmlFor = input.id;
      custom.append(label, input);
      custom.hidden = answers.budget !== 'custom';
      fieldset.append(custom);
    } else pane.append(progress, heading);
    if (key === 'direction') pane.append(make('p', 'coffee-finder__helper', 'Not sure? An easy gift is a good place to start.'));
    if (question.helper) pane.append(make('p', 'coffee-finder__helper', question.helper));
    fieldset.append(error);
    pane.append(fieldset);
    const actions = make('div', 'coffee-finder__actions coffee-finder__actions--navigation gift-finder__dock');
    const back = action('Back', () => renderQuestion(stepsFor(answers, products)[position - 1], editing));
    back.hidden = position === 0;
    const next = make('button', 'button button--compact button--primary coffee-finder__button');
    next.type = 'submit';
    const clearQuestionError = () => {
      error.textContent = '';
      options.querySelectorAll('.coffee-finder__option').forEach(clearErrorFeedback);
      if (custom) clearErrorFeedback(custom.querySelector('input'));
      fieldset.removeAttribute('aria-invalid');
      fieldset.querySelectorAll('[aria-invalid]').forEach(input => input.removeAttribute('aria-invalid'));
    };
    if (custom) custom.querySelector('input').addEventListener('input', event => {
      const amount = event.target.value.trim().replace(/^£\s*/, '').replace(/,/g, '');
      if (budgetInPence({ budget: 'custom', customBudget: amount }) !== null) clearQuestionError();
    });
    const updateNext = () => {
      const value = options.querySelector('input:checked')?.value || '';
      const proposed = { ...answers, [key]: value };
      if (key === 'direction' && value !== answers.direction) for (const other of ['grinder', 'brew', 'caffeine', 'kind', 'drive', 'taste', 'browseKind', 'espressoGrinder']) delete proposed[other];
      if (key === 'kind' && value !== answers.kind) { delete proposed.brew; delete proposed.drive; }
      const path = stepsFor(proposed, products);
      next.textContent = value && (path.indexOf(key) === path.length - 1 || editing && path.every(step => proposed[step])) ? 'Show gifts' : 'Continue';
      clearQuestionError();
      if (custom) custom.hidden = value !== 'custom';
    };
    next.textContent = 'Continue';
    updateNext();
    options.addEventListener('change', updateNext);
    actions.append(back, next);
    form.append(pane, actions);
    form.addEventListener('submit', event => {
      event.preventDefault();
      const value = new FormData(form).get(`gift-${key}`);
      if (!value) {
        error.textContent = key === 'budget' ? 'Choose a budget to continue.' : 'Choose an option to continue.';
        fieldset.setAttribute('aria-invalid', 'true');
        options.querySelectorAll('.coffee-finder__option').forEach(showErrorFeedback);
        const first = options.querySelector('input');
        first?.setAttribute('aria-invalid', 'true');
        focusWithin(first);
        revealInPage(error);
        return;
      }
      if (key === 'budget' && value === 'custom') {
        const input = custom.querySelector('input');
        const amount = input.value.trim().replace(/^£\s*/, '').replace(/,/g, '');
        if (budgetInPence({ budget: 'custom', customBudget: amount }) === null) {
          error.textContent = 'Enter a budget above £0 (up to two decimal places).';
          showErrorFeedback(input);
          input.setAttribute('aria-invalid', 'true');
          focusWithin(input);
          revealInPage(error);
          return;
        }
        answers.customBudget = amount;
      }
      const changed = answers[key] !== value;
      answers[key] = value;
      if (key === 'direction' && changed) for (const other of ['grinder', 'brew', 'caffeine', 'kind', 'drive', 'taste', 'browseKind', 'espressoGrinder']) delete answers[other];
      if (key === 'kind' && changed) { delete answers.brew; delete answers.drive; }
      const missing = validPath();
      persist();
      track('answer_submitted', { question: key, value, budget_pence: budgetInPence(answers), editing });
      const path = stepsFor(answers, products);
      const following = path[path.indexOf(key) + 1];
      if (editing && !missing || !following) renderResults();
      else renderQuestion(editing ? missing : following, editing);
    });
    showView(form);
    track('question_viewed', { question: key, position: position + 1, editing });
    announce(`Question ${position + 1}. ${question.title}`);
  }

  function initialiseResultCards() {
    initializeQuickBuyCards(view);
    document.dispatchEvent(new CustomEvent('sigma:collection-view:updated', { detail: { view } }));
  }

  function addVariantFeedback(card, product, recommended, context) {
    const productCard = card.querySelector('.product-card');
    const notice = make('div', 'gift-finder__variant-feedback');
    notice.hidden = true;
    const message = make('p');
    message.setAttribute('role', 'status');
    const acknowledgement = make('label', 'gift-finder__acknowledgement');
    const checkbox = make('input');
    checkbox.type = 'checkbox';
    const acknowledgementText = make('span');
    acknowledgement.append(checkbox, createCheckboxIndicator(), acknowledgementText);
    const effectiveAnswers = context.exact || context.type === 'browse' ? answers : { ...answers, direction: 'routine' };
    let selectedId = '';
    const idForCard = () => String(productCard.querySelector('[data-quick-buy-variant]')?.value || productCard.dataset.selectedVariantId || '');
    const constraint = () => {
      const id = idForCard();
      const selected = product.variants.find(variant => String(variant.id) === id);
      if (!selected || !suitableVariants(product, 100000000, effectiveAnswers).some(variant => String(variant.id) === id)) return { type: 'fit', message: 'This option does not match your answers. Choose the recommended option to keep the gift suitable.' };
      const ceiling = budgetInPence(answers);
      if (selected.price > ceiling) return { type: 'budget', message: `This option is ${currency.format((selected.price - ceiling) / 100)} over your ${currency.format(ceiling / 100)} budget.`, selected };
      return null;
    };
    const update = () => {
      const id = idForCard();
      if (selectedId !== id) {
        checkbox.checked = false;
        selectedId = id;
        if (suitableVariants(product, budgetInPence(answers), effectiveAnswers).some(variant => String(variant.id) === id)) variantChoices[String(product.id)] = id;
        else delete variantChoices[String(product.id)];
        persist();
      }
      const issue = constraint();
      notice.hidden = !issue;
      if (!issue || issue.type === 'budget' && checkbox.checked) clearErrorFeedback(notice);
      if (!issue) return;
      message.textContent = issue.message;
      acknowledgement.hidden = issue.type !== 'budget';
      acknowledgementText.textContent = issue.type === 'budget' ? `I’m happy to spend ${currency.format(issue.selected.price / 100)} on this gift.` : '';
    };
    const reset = action('Use recommended option', () => {
      const control = productCard.querySelector('[data-quick-buy-variant]');
      if (control) { control.value = String(recommended.id); control.dispatchEvent(new Event('change', { bubbles: true })); }
      else {
        const source = productCard.querySelector('[data-product-card-variants]');
        const nativeVariant = JSON.parse(source?.textContent || '[]').find(variant => String(variant.id) === String(recommended.id));
        for (const button of productCard.querySelectorAll('[data-product-card-option]')) {
          const expected = button.dataset.productCardOption === 'roast-date' ? nativeVariant?.roastDate : nativeVariant?.options?.[Number(button.dataset.productCardOptionIndex)];
          if (expected && button.dataset.productCardOptionValue === expected && button.getAttribute('aria-pressed') !== 'true') button.click();
        }
      }
      update();
      focusWithin(productCard.querySelector('[data-quick-buy-option-index].is-selected, [data-product-card-option][aria-pressed="true"], a'));
    });
    notice.append(message, acknowledgement, reset);
    message.id = `${root.id}-variant-issue-${product.id}`;
    checkbox.setAttribute('aria-describedby', message.id);
    checkbox.addEventListener('change', update);
    card.querySelector('.gift-finder__card-meta').append(notice);
    productCard.addEventListener('change', update);
    productCard.addEventListener('click', () => queueMicrotask(update));
    productCard.addEventListener('submit', event => {
      const issue = constraint();
      if (!issue || issue.type === 'budget' && checkbox.checked) {
        track('add_requested', { product_id: String(product.id), variant_id: idForCard(), over_budget_acknowledged: issue?.type === 'budget' });
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      update();
      showErrorFeedback(notice);
      announce(issue.message);
      focusWithin(issue.type === 'budget' ? checkbox : reset);
    }, true);
    const observer = new MutationObserver(update);
    observer.observe(productCard, { attributes: true, attributeFilter: ['data-selected-variant-id'], childList: false });
    cleanups.push(() => observer.disconnect());
    update();
  }

  function renderResults({ focus = true, restoreTaste = false, showAll = false } = {}) {
    root.dataset.finderState = 'results';
    const context = resultContext(products, answers);
    const initial = selectedProducts(products, answers);
    const all = [...initial];
    for (const entry of allSuitableProducts(products, answers)) {
      if (!all.some(item => String(item.product.id) === String(entry.product.id))) all.push(entry);
    }
    const matches = showAll ? all : initial;
    const wrapper = make('div', 'gift-finder__stage gift-finder__stage--results');
    const pane = paneFor('Gift ideas and product options');
    const header = make('header', 'coffee-finder__result-header');
    const title = context.type === 'browse' ? 'Gifts to explore' : context.type === 'mixed' ? 'Your gift shortlist' : context.type === 'alternative' ? 'Other gifts to consider' : matches.length === 1 ? 'A gift that fits' : matches.length ? 'A few gifts that fit' : 'No gifts within this budget';
    header.append(make('h2', 'coffee-finder__title', title), make('p', 'coffee-finder__copy', context.message));
    pane.append(header);
    const answerDetails = make('details', 'gift-finder__answers');
    const answerSummary = make('summary');
    answerSummary.setAttribute('data-icon-text', '');
    const summaryCopy = make('span', 'gift-finder__answer-summary-copy');
    summaryCopy.append(make('span', 'gift-finder__answer-summary-title', 'Your answers'));
    const budgetLabel = answers.budget === 'custom' ? currency.format(budgetInPence(answers) / 100) : QUESTIONS.budget.options.find(([value]) => value === answers.budget)?.[1];
    const preview = [budgetLabel, answers.kind && answers.kind !== 'unknown'
      ? QUESTIONS.kind.options.find(([value]) => value === answers.kind)?.[1]
      : { routine: 'Easy gift', coffee: 'Coffee', equipment: 'Equipment' }[answers.direction]];
    summaryCopy.append(make('span', 'gift-finder__answer-preview', preview.filter(Boolean).join(' · ')));
    const chevron = iconFor('chevron');
    chevron.classList.add('gift-finder__answer-chevron');
    const summaryAction = make('span', 'gift-finder__answer-summary-action');
    const summaryActionLabel = make('span', '', 'Edit');
    summaryAction.append(summaryActionLabel, chevron);
    answerSummary.append(summaryCopy, summaryAction);
    answerDetails.append(answerSummary);
    const choices = make('div', 'gift-finder__answer-rows');
    for (const key of stepsFor(answers, products)) {
      if (!answers[key]) continue;
      const label = key === 'budget' && answers.budget === 'custom' ? currency.format(budgetInPence(answers) / 100) : QUESTIONS[key].options.find(([value]) => value === answers[key])?.[1] || answers[key];
      const edit = make('button', 'gift-finder__answer-edit');
      edit.type = 'button';
      edit.setAttribute('data-icon-text', '');
      edit.setAttribute('aria-label', `Edit ${ANSWER_NAMES[key].toLowerCase()}: ${label}`);
      const copy = make('span', 'gift-finder__answer-copy');
      copy.append(make('span', 'gift-finder__answer-label', ANSWER_NAMES[key]), make('span', 'gift-finder__answer-value', label));
      const editLabel = make('span', 'gift-finder__answer-edit-label');
      editLabel.append(iconFor('edit'), make('span', '', 'Edit'));
      edit.append(copy, editLabel);
      edit.addEventListener('click', () => renderQuestion(key, true));
      choices.append(edit);
    }
    answerDetails.append(choices);
    let disclosureMotion;
    let answerOpen = false;
    answerSummary.addEventListener('click', event => {
      event.preventDefault();
      const from = answerDetails.getBoundingClientRect().height;
      answerOpen = !answerOpen;
      disclosureMotion?.cancel();
      answerDetails.dataset.expanded = String(answerOpen);
      summaryAction.firstElementChild.textContent = answerOpen ? 'Close' : 'Edit';
      answerDetails.open = true;
      answerDetails.style.removeProperty('height');
      const to = answerOpen ? answerDetails.getBoundingClientRect().height : answerSummary.getBoundingClientRect().height;
      if (reducedMotion.matches || event.detail === 0) {
        answerDetails.open = answerOpen;
        answerDetails.style.removeProperty('overflow');
        return;
      }
      answerDetails.style.overflow = 'clip';
      const motion = answerDetails.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration: 200, easing: 'cubic-bezier(0.25, 1, 0.5, 1)', fill: 'both' });
      disclosureMotion = motion;
      motion.finished.catch(() => {}).then(() => {
        if (disclosureMotion !== motion) return;
        answerDetails.open = answerOpen;
        motion.cancel();
        answerDetails.style.removeProperty('overflow');
        disclosureMotion = null;
      });
    });
    const finishAnswerDisclosure = () => {
      disclosureMotion?.cancel(); disclosureMotion = null;
      answerDetails.open = answerOpen;
      answerDetails.style.removeProperty('overflow');
    };
    const onAnswerMotionChange = () => { if (reducedMotion.matches) finishAnswerDisclosure(); };
    reducedMotion.addEventListener('change', onAnswerMotionChange);
    const clearAnswerDisclosure = () => { finishAnswerDisclosure(); reducedMotion.removeEventListener('change', onAnswerMotionChange); };
    pane.append(answerDetails);
    if (context.exact && answers.direction === 'coffee') {
      const refinement = make('details', 'gift-finder__refine');
      refinement.open = restoreTaste;
      refinement.append(make('summary', '', 'Refine by flavour (optional)'));
      const label = make('label', '', 'Flavour preference');
      const select = make('select');
      select.id = `${root.id}-taste`;
      label.htmlFor = select.id;
      for (const [value, text] of QUESTIONS.taste.options) {
        const option = make('option', '', text);
        option.value = value;
        option.selected = (answers.taste || 'unknown') === value;
        select.append(option);
      }
      select.addEventListener('change', () => {
        answers.taste = select.value;
        persist();
        track('flavour_refined', { value: answers.taste, budget_pence: budgetInPence(answers) });
        renderResults({ focus: false, restoreTaste: true });
        focusWithin(view.querySelector(`#${CSS.escape(select.id)}`));
      });
      refinement.append(label, select);
      pane.append(refinement);
    }
    const grid = make('div', 'collection-products gift-guide__track gift-finder__carousel');
    grid.id = `${root.id}-recommendations`;
    grid.tabIndex = 0;
    grid.setAttribute('role', 'region');
    grid.setAttribute('aria-label', 'Recommended gifts. Scroll to explore more gifts.');
    const pendingFeedback = [];
    for (const entry of matches) {
      const { product } = entry;
      const selectionAnswers = answersForSelection(entry, answers);
      const variant = suitableVariants(product, budgetInPence(answers), selectionAnswers).find(option => String(option.id) === variantChoices[String(product.id)]) || entry.variant;
      const isAlternative = entry.matchType === 'alternative';
      const template = [...root.querySelectorAll('template[data-gift-card]')].find(item => item.dataset.giftCard === String(product.id));
      if (!template) continue;
      const card = make('article', 'coffee-finder__result gift-guide__slide');
      card.dataset.giftProduct = String(product.id);
      card.dataset.giftVariant = String(variant.id);
      card.addEventListener('click', event => {
        if (!(event.target instanceof Element)) return;
        const link = event.target.closest('a[href]');
        if (link && new URL(link.href, location.href).pathname === new URL(product.url, location.href).pathname)
          track('product_link_clicked', { product_id: String(product.id), variant_id: new URL(link.href, location.href).searchParams.get('variant') || String(variant.id) });
      });
      const families = product.coffee?.families || [];
      const coffeeRecipient = String(product.coffee?.caffeine || '').toLowerCase() === 'decaf' ? 'For a decaf drinker'
        : families.includes('Funky & experimental') ? 'For an adventurous coffee drinker'
        : families.includes('Floral & delicate') ? 'For someone who enjoys floral coffee'
        : families.some(family => ['Fruity & bright', 'Juicy & tropical'].includes(family)) ? 'For someone who enjoys fruity coffee'
        : families.some(family => ['Chocolatey & nutty', 'Sweet & comforting'].includes(family)) ? 'For familiar, comforting flavours'
        : 'For their usual coffee routine';
      const roleLabel = product.recipient || (product.kind === 'coffee' ? coffeeRecipient : '') || (isAlternative ? 'Easy-gift alternative' : product.kind === 'book' ? 'For curious coffee lovers' : product.kind === 'server' ? 'For serving and sharing' : product.kind === 'cup' ? 'For their daily coffee' : 'For their coffee setup');
      const content = template.content.cloneNode(true);
      if (product.summary || product.requirement) content.querySelectorAll('.quick-buy-card__synopsis').forEach(node => node.remove());
      content.querySelectorAll('[data-product-card-link], [data-quick-buy-product-link]').forEach(link => {
        link.dataset.sigmaPurchaseSource = 'gift_finder';
      });
      const quickVariant = content.querySelector('[data-quick-buy-variant]');
      if (quickVariant) quickVariant.value = String(variant.id);
      const showcase = content.querySelector('[data-product-card-showcase]');
      if (showcase) showcase.dataset.selectedVariantId = String(variant.id);
      content.querySelectorAll('[data-for-you-favourite], [data-product-favourite], .product-favourite, .product-card__favourite').forEach(node => node.remove());
      content.querySelectorAll('.quick-buy-card__view, .product-card__actions a').forEach(link => {
        const label = [...link.querySelectorAll('span')].find(span => span.textContent.trim() === 'View product');
        if (label) label.textContent = 'View gift';
        else for (const node of link.childNodes) if (node.nodeType === Node.TEXT_NODE) node.textContent = node.textContent.replace('View product', 'View gift');
      });
      const guidance = make('div', 'gift-finder__purchase-guidance');
      guidance.append(make('p', 'gift-finder__role', roleLabel));
      if (product.requirement) guidance.append(make('p', '', product.requirement));
      const purchaseBody = content.querySelector('.product-card__showcase-body');
      purchaseBody?.querySelector('.product-card__showcase-title')?.after(guidance);
      card.append(content);
      const meta = make('div', 'gift-finder__card-meta');
      const reasonHeading = make('div', 'gift-finder__card-heading');
      reasonHeading.append(make('h3', 'coffee-finder__reason-title', entry.matchType === 'browse' ? 'Check before buying' : isAlternative ? 'An easy-gift alternative' : context.exact ? 'Why it fits' : 'Why consider it'));

      meta.append(reasonHeading, make('p', '', reasonFor(product, selectionAnswers)));
      if (!purchaseBody && product.requirement) meta.append(make('p', '', product.requirement));
      card.append(meta);
      grid.append(card);
      pendingFeedback.push(() => addVariantFeedback(card, product, entry.variant, { ...context, exact: !isAlternative && context.exact }));
    }
    const resultRow = make('section', 'gift-guide__row');
    resultRow.setAttribute('aria-label', 'Recommended gifts');
    const railSpace = make('div', 'gift-finder__rail-space');
    if (grid.childElementCount) { railSpace.append(grid); resultRow.append(railSpace); pane.append(resultRow); }
    else if (matches.length) pane.append(make('p', 'coffee-finder__copy', 'These gifts cannot be displayed right now. Browse the gift guide below.'));
    if (!showAll && all.length > initial.length) {
      const moreLabel = context.type === 'browse' ? 'Show all gifts to check' : 'Show all suitable gifts';
      pane.append(action(`${moreLabel} (${all.length})`, () => renderResults({ showAll: true })));
    }
    if (matches.length < 2 || context.type === 'alternative' || context.type === 'no-match') {
      const recovery = make('aside', 'gift-finder__recovery');
      recovery.append(make('h3', 'coffee-finder__reason-title', matches.length ? 'Want a wider choice?' : 'Find another way to give'));
      const ceiling = budgetInPence(answers);
      const nextPrice = products.filter(product => ['book', 'cup', 'server'].includes(product.kind) && !matches.some(entry => String(entry.product.id) === String(product.id)))
        .flatMap(product => product.variants.filter(variant => variant.available && variant.price > ceiling).map(variant => variant.price))
        .sort((a, b) => a - b)[0];
      recovery.append(make('p', 'coffee-finder__copy', matches.length === 1 ? 'Only one suitable gift in this shortlist fits your budget right now. A higher budget can open up more choices.' : 'Change your budget or gift type, or let them choose with a gift card.'));
      const recoveryActions = make('div', 'gift-finder__recovery-actions');
      recoveryActions.append(action('Change budget', () => renderQuestion('budget', true)), action('Choose another gift type', () => renderQuestion('direction', true)));
      if (nextPrice && matches.length < 2) recoveryActions.append(action(`Explore gifts up to ${currency.format(nextPrice / 100)}`, () => {
        answers.budget = 'custom'; answers.customBudget = String(nextPrice / 100); persist();
        const missing = validPath();
        if (missing) renderQuestion(missing, true); else renderResults();
      }));
      const giftCard = make('a', 'button button--compact', 'Explore gift cards');
      giftCard.href = root.dataset.giftCardUrl;
      recoveryActions.append(giftCard);
      recovery.append(recoveryActions); pane.append(recovery);
    }
    const actions = make('div', 'coffee-finder__actions gift-finder__result-actions gift-finder__dock');
    const restart = make('button', 'button button--compact gift-finder__restart coffee-finder__button');
    restart.type = 'button';
    restart.setAttribute('data-icon-text', '');
    restart.append(iconFor('restart'), make('span', '', 'Start again'));
    restart.addEventListener('click', event => {
      event.preventDefault(); track('restarted', { from: 'results' }); answers = {}; variantChoices = {}; persist(); renderQuestion('budget'); });
    actions.append(restart);
    wrapper.append(pane, actions);
    showView(wrapper, { focus });
    cleanups.push(clearAnswerDisclosure);
    initialiseResultCards();
    const sourceHint = document.querySelector('[data-product-swipe-hint]');
    if (sourceHint && grid.childElementCount > 1) {
      const hint = sourceHint.cloneNode(true);
      hint.className = 'landing-roaster-swipe-hint product-swipe-hint is-paused';
      hint.dataset.swipeTarget = grid.id;
      hint.hidden = true;
      resultRow.append(hint);
    }
    const sourceControls = document.querySelector('[data-product-controls]');
    if (sourceControls && grid.childElementCount > 1) {
      const controls = sourceControls.cloneNode(true);
      controls.classList.add('gift-finder__carousel-controls');
      const previous = controls.querySelector('[data-product-previous]');
      const next = controls.querySelector('[data-product-next]');
      for (const button of [previous, next]) button.setAttribute('aria-controls', grid.id);
      previous.setAttribute('aria-label', 'Previous gift recommendations');
      next.setAttribute('aria-label', 'Next gift recommendations');
      resultRow.append(controls);
      const update = () => {
        const maximum = grid.scrollWidth - grid.clientWidth;
        grid.dataset.fadeStart = String(grid.scrollLeft > 1);
        grid.dataset.fadeEnd = String(grid.scrollLeft < maximum - 1);
        controls.hidden = maximum <= 1;
        previous.disabled = grid.scrollLeft <= 1;
        next.disabled = grid.scrollLeft >= maximum - 1;
      };
      const move = (direction, animate = true) => grid.scrollBy({ left: direction * grid.clientWidth, behavior: reducedMotion.matches || !animate ? 'auto' : 'smooth' });
      previous.addEventListener('click', event => move(-1, event.detail !== 0));
      next.addEventListener('click', event => move(1, event.detail !== 0));
      grid.addEventListener('scroll', update, { passive: true });
      grid.addEventListener('keydown', event => {
        if (event.target !== grid || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); move(event.key === 'ArrowLeft' ? -1 : 1, false);
      });
      const observer = new ResizeObserver(update);
      observer.observe(grid);
      cleanups.push(() => observer.disconnect());
      update();
    }
    pendingFeedback.forEach(add => add());
    if (grid.childElementCount) cleanups.push(fitMobileResultRail(grid));
    const rendered = [...grid.querySelectorAll('[data-gift-product]')];
    const count = rendered.length;
    track('results_viewed', { result_type: context.type, count, product_ids: rendered.map(card => card.dataset.giftProduct), variant_ids: rendered.map(card => card.dataset.giftVariant) });
    announce(count ? `${count === 1 ? '1 gift idea' : `${count} gift ideas`} ready. ${context.message}` : context.message);
  }

  const start = view.querySelector('[data-gift-start]');
  if (root.dataset.currency !== 'GBP') {
    if (start) start.hidden = true;
    view.querySelector('.coffee-finder__copy').textContent = 'This finder currently uses UK prices. Browse the gift guide below to see prices in your local currency.';
    return;
  }
  if (start) {
    start.hidden = false;
    start.addEventListener('keydown', event => {
      if (event.key === ' ') { event.preventDefault(); start.click(); }
    });
    const missing = validPath();
    const savedComplete = !missing && budgetInPence(answers) !== null;
    if (savedComplete && performance.getEntriesByType('navigation')[0]?.type === 'back_forward') {
      renderResults({ focus: false });
      return;
    }
    // Fresh visits retain the introduction; completed history returns resume results.
    start.addEventListener('click', event => {
      event.preventDefault();
      track(Object.keys(answers).length ? 'resumed' : 'started', { saved_complete: savedComplete });
      if (savedComplete) renderResults();
      else renderQuestion(missing || 'budget');
    });
  }
}

document.querySelectorAll('[data-gift-finder]').forEach(init);
document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-gift-finder]').forEach(init));
